import React, { useState } from 'react';
import { ImEye, ImEyeBlocked } from "react-icons/im";
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import toast, { Toaster } from 'react-hot-toast';

const getRedirectPath = (userData) => {
    const roleData = userData?.role;
    const isSuperAdmin = (
        (typeof roleData === 'object' && roleData?.privileges?.includes('*')) ||
        (typeof roleData === 'string' && (roleData.toLowerCase() === 'admin' || roleData.toLowerCase() === 'superadmin'))
    );

    if (isSuperAdmin) {
        return '/dashboard';
    }

    if (typeof roleData === 'object' && Array.isArray(roleData.privileges) && roleData.privileges.length > 0) {
        const resourceToRouteMap = {
            dashboard: '/dashboard',
            users: '/users-list',
            expertise: '/expertise-list',
            astrologers: '/astrologer-list',
            bookings: '/booking-list',
            notifications: '/notification-list',
            languages: '/language-list',
            skills: '/skill-list',
            astro_tags: '/astro-tag-management',
            role_management: '/role-management',
            user_management: '/user-management',
            offers: '/offer-list',
            lead_management: '/leads-management',
            student_management: '/student-management',
            course_management: '/course-management',
            category_management: '/category-management',
            course_banners: '/course-banners',
            instructor_leads: '/instructor-leads',
            about_us: '/about-us',
            privacy_policy: '/privacy-policy',
            terms_conditions: '/terms-conditions',
            refund_policy: '/refund-policy',
            course_specific_reviews: '/course-specific',
            generic_reviews: '/generic-reviews',
            course_specific_faqs: '/course-specificfaq',
            generic_faqs: '/generic-faq',
            refer_earn_faqs: '/refer-faq',
            education_coupons: '/eduction-modulecoupon',
            consultation_coupons: '/consultation-coupon',
            google_pay_coupons: '/googlepay-coupon',
            consultation_vouchers: '/consultation-voucher',
            top_banners: '/top-banners',
            consultation_type_banners: '/consultation-type',
            service_banners: '/service-banners',
            user_feedback: '/user-feedback',
            astrologer_tickets: '/astrologer-support',
            user_tickets: '/user-support',
            callback_requests: '/callback-requests',
            daily_recharge_report: '/daily-recharge',
            invoice_reports: '/invoice-reports',
            session_report: '/session-reports',
            coupon_usage_report: '/coupon-usesreport',
            credit_note_report: '/credit-reports',
            payout_report: '/payout-reports',
            course_selling_report: '/course-selling-report',
            wallet_report: '/wallet-report',
        };

        for (const privilege of roleData.privileges) {
            const resource = privilege.split(':')[0];
            if (resourceToRouteMap[resource]) {
                return resourceToRouteMap[resource];
            }
        }
    }

    return '/dashboard';
};

function SignIn() {
    const navigate = useNavigate();
    const { register, handleSubmit, formState: { errors } } = useForm();
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const onSubmit = (data) => {
        setIsLoading(true);
        const isSpecialAdmin =
            data.email.toLowerCase() === process.env.REACT_APP_SPECIAL_ADMIN_EMAIL?.toLowerCase();

        const loginUrl = isSpecialAdmin
            ? `${process.env.REACT_APP_BASEURL}/admin/login/`
            : `${process.env.REACT_APP_BASEURL}/admin/member/login/`;

        axios
            .post(loginUrl, {
                email: data.email,
                password: data.password,
            })
            .then((response) => {
                const isSuccess = response?.data?.status || response?.data?.success;
                if (!isSuccess) {
                    toast.error(response?.data?.message || "Login failed");
                    setIsLoading(false);
                    return;
                }

                // Login response only contains email, no token yet
                // OTP will be sent to email, need to verify OTP to get token
                const email = response.data.email || data.email;

                if (!email) {
                    toast.error("Login response missing email");
                    setIsLoading(false);
                    return;
                }

                // Store email and admin type for OTP verification
                localStorage.setItem("vedhvidhi-admin-temp-email", email);
                localStorage.setItem("vedhvidhi-admin-is-special", isSpecialAdmin.toString());

                // Show success message
                toast.success(response?.data?.message || "OTP sent to your email");

                // Redirect to OTP verification screen
                setTimeout(() => {
                    navigate(process.env.PUBLIC_URL + "/login-otp-verification");
                }, 1000);
            })
            .catch((error) => {
                toast.error(error?.response?.data?.message || "Login failed");
                setIsLoading(false);
            });
    };

    const inputGroupStyle = { display: 'flex', alignItems: 'center', position: 'relative' };
    const inputStyle = { paddingRight: '40px' };
    const iconStyle = { position: 'absolute', right: '10px', cursor: 'pointer' };

    return (
        <>
            <Toaster position="top-center" reverseOrder={true} duration="10000" />
            <div className="col-lg-6 d-flex justify-content-center align-items-center border-0 rounded-lg auth-h100">
                <div className="w-100 p-3 p-md-5 card border-0 shadow-sm" style={{ maxWidth: "32rem" }}>
                    <form className="row g-1 p-3 p-md-4 mt-5" onSubmit={handleSubmit(onSubmit)}>
                        <div className="col-12 text-center mb-0">
                            <h1>Sign in</h1>
                        </div>
                        <div className="col-12">
                            <div className="mb-2">
                                <label className="form-label">Email address</label>
                                <input
                                    {...register("email", { required: "Email is required." })}
                                    type="email"
                                    className="form-control form-control-lg"
                                    placeholder="name@example.com"
                                    onChange={(e) => e.target.value = e.target.value.toLowerCase()}
                                />
                                {errors.email && <span className="text-danger">{errors.email.message}</span>}
                            </div>
                        </div>
                        <div className="col-12">
                            <div className="mb-2">
                                <div className="form-label d-flex justify-content-between align-items-center">
                                    Password
                                    <Link className="text-secondary" to="/reset-password">Forgot Password?</Link>
                                </div>
                                <div style={inputGroupStyle}>
                                    <input
                                        {...register("password", { required: "Password is required." })}
                                        type={showPassword ? "text" : "password"}
                                        className="form-control form-control-lg"
                                        placeholder="Pa$$w0rd!"
                                        style={inputStyle}
                                    />
                                    <div style={iconStyle}>
                                        {showPassword
                                            ? <ImEyeBlocked onClick={() => setShowPassword(false)} />
                                            : <ImEye onClick={() => setShowPassword(true)} />}
                                    </div>
                                </div>
                                {errors.password && <span className="text-danger">{errors.password.message}</span>}
                            </div>
                        </div>
                        <div className="col-12 text-center mt-4">
                            <button
                                type="submit"
                                className="btn btn-lg btn-block btn-light lift text-uppercase"
                                disabled={isLoading}
                                style={{
                                    opacity: isLoading ? 0.7 : 1,
                                    cursor: isLoading ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Signing in...
                                    </>
                                ) : (
                                    'SIGN IN'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
}

export default SignIn;